// ===========================================
// Email Sequence Editor
// ===========================================
// Author a drip series: a header (when it starts, what hour it sends) and an
// ordered list of emails, each offset from the anchor by a number of days.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import MergeFieldChips from '../../components/common/MergeFieldChips';
import RenderedPreviewPanel from '../../components/common/RenderedPreviewPanel';
import RichTextEditor, { RichTextEditorHandle } from '../../components/common/RichTextEditor';
import { PermissionGate } from '../../components/common/PermissionGate';
import { useTenant } from '../../contexts/TenantContext';
import {
  useCancelSequenceEnrolment,
  useCreateEmailSequence,
  useEmailSequence,
  useEmailSequenceStatus,
  useEmailSequenceStepPreview,
  usePauseSequenceEnrolment,
  useResumeSequenceEnrolment,
  useSendEmailSequenceTest,
  useSequenceEnrolments,
  useUpdateEmailSequence,
} from '../../hooks/useEmailSequence';
import { apiErrorMessage } from '../../utils/apiError';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { formatUTCDate } from '../../utils/dateUtils';
import { insertAtSelectionEnd, isDuplicateMouseActivation } from '../../utils/mergeFieldInsert';
import {
  isSequenceDirty,
  type EmailSequenceFormState,
  type SequenceStepFormState,
} from './emailSequenceDirty';
import {
  SEQUENCE_LIMITS,
  SEQUENCE_MERGE_FIELDS,
  SEQUENCE_MERGE_FIELD_ALIASES,
} from '../../types/emailSequence.types';
import type {
  EmailSequenceEnrolment,
  EmailSequenceEnrolmentStep,
  MergeFieldErrorDetails,
} from '../../types/emailSequence.types';
import { formatSendHour } from './EmailSequencesPage';

// ===========================================
// MERGE FIELDS
// ===========================================

/**
 * ⚠️ Mirrors `EMAIL_SEQUENCE_MERGE_FIELDS`, which is the authority and rejects
 * the save with a 422 naming anything it does not recognise.
 *
 * `{{studentName}}` IS offered here and deliberately is NOT on a custom email:
 * a sequence enrolment is per student, so there is exactly one child to name.
 * `{{parentName}}` leads because it is the token MNM already type.
 */
const SEQUENCE_MERGE_FIELD_CHIPS = [
  { label: 'First name', placeholder: '{{parentName}}' },
  { label: 'Last name', placeholder: '{{lastName}}' },
  { label: 'Student name', placeholder: '{{studentName}}' },
  { label: 'School name', placeholder: '{{schoolName}}' },
];

/**
 * Every token the backend accepts: the four canonical fields plus the aliases.
 *
 * ⚠️ DERIVED, not hand-listed. A second hand-maintained copy of the same mirror
 * drifts from the first silently — and when it does, this editor starts
 * flagging a token the server would have accepted, before the round trip, with
 * no way for the admin to tell who is wrong. `SEQUENCE_MERGE_FIELDS` and
 * `SEQUENCE_MERGE_FIELD_ALIASES` are the one mirror; a new backend alias now
 * reaches this set by adding it there alone.
 */
const ACCEPTED_MERGE_TOKENS = new Set<string>([
  ...SEQUENCE_MERGE_FIELDS,
  ...Object.keys(SEQUENCE_MERGE_FIELD_ALIASES),
]);

/**
 * Catch a bad token BEFORE the round trip.
 *
 * ⚠️ This exists because the server's 422 NAMES NO STEP — it cannot, since it
 * validates each step in turn and throws on the first bad one. Without a
 * client-side pass the admin is told "unknown merge field" with no way to tell
 * which of thirty emails contains it. The server remains the authority; this
 * only makes the answer actionable.
 */
export function findInvalidTokens(html: string): string[] {
  const found = html.match(/\{\{([^}]*)\}\}/g) ?? [];
  return found.filter((raw) => !ACCEPTED_MERGE_TOKENS.has(raw.slice(2, -2).trim()));
}

// ===========================================
// CANCEL REASONS
// ===========================================

/**
 * Why a series stopped, in an admin's words.
 *
 * ⚠️ LOOKED UP WITH A FALLBACK, never an exhaustive switch. `cancelReason` is a
 * VarChar on the backend, not an enum, and its own service comment says the
 * list widens — a sixth reason must read as something, not render `undefined`.
 */
const CANCEL_REASON_COPY: Record<string, { text: string; warn?: boolean }> = {
  ADMIN: { text: 'Stopped by an admin' },
  STUDENT_INACTIVE: { text: 'Stopped — student withdrawn' },
  STUDENT_DELETED: { text: 'Stopped — student record deleted' },
  // ⚠️ THE ONLY SIGNAL AN ADMIN EVER GETS OF AN OPT-OUT. Unsubscribes are
  // otherwise invisible in this app, so this one reads as a warning.
  NO_RECIPIENTS: { text: 'Stopped — every contact unsubscribed', warn: true },
  NO_CONTACTS: { text: 'Stopped — no contact email on file' },
};

export function cancelReasonCopy(reason: string | null): { text: string; warn?: boolean } {
  if (!reason) return { text: 'Stopped' };
  return CANCEL_REASON_COPY[reason] ?? { text: `Stopped — ${reason.toLowerCase().replace(/_/g, ' ')}` };
}

/** What a finished series should say, given how its steps actually landed. */
export function completedSummary(enrolment: EmailSequenceEnrolment): string {
  const failed = enrolment.counts.failed;
  if (failed > 0) return `Completed — ${failed} step${failed === 1 ? '' : 's'} failed`;
  return 'Completed';
}

/**
 * The day a step went out, or is expected to.
 *
 * ⚠️ ONLY SAFE FOR A PENDING STEP, and the one call site is guarded to pass
 * exactly those (`steps.find((s) => s.state === null)`).
 *
 * `sendDay` IS A MIXED SHAPE: a UTC-midnight calendar day while pending, but a
 * full TIMESTAMP once sent (it falls back to the ledger's `sentAt`). UTC getters
 * are right for the first and WRONG for the second — a 22:00 UTC send is 9am the
 * next day in Sydney and renders here as the previous date.
 *
 * ⚠️ An earlier version of this comment claimed both shapes "render correctly
 * through UTC getters". That is false, and it is exactly the H-1 bug shipped in
 * this feature on the sibling component. Corrected by the whole-feature /qa
 * (2026-09-17): do not call this on a SETTLED step. For one of those, render
 * `sentAt` in LOCAL time — see `StudentEmailSequencesList.stepLine`, which
 * states the per-field rule in full.
 */
export function formatStepDay(step: EmailSequenceEnrolmentStep): string {
  return formatUTCDate(
    step.sendDay,
    { day: '2-digit', month: '2-digit', year: 'numeric' },
    'en-AU'
  );
}

const emptyStep = (): SequenceStepFormState => ({
  offsetDays: '',
  subject: '',
  preheader: '',
  content: '',
});

// ===========================================
// VALIDATION — pure, so it can be tested without the page
// ===========================================
//
// ⚠️ EXTRACTED FROM THE `useMemo` BODIES (/qa Phase G). Inside a hook this logic
// was reachable only by rendering the whole editor, so none of it had a test —
// and a source-text pin cannot see a `< 0` that should be `<= 0`. These are the
// rules an admin is stopped by, so they are the rules worth pinning.

/** Everything wrong with one step, in the words shown beside it. */
export function validateStep(
  step: SequenceStepFormState,
  anchor: 'START' | 'DATE'
): string[] {
  const errors: string[] = [];
  if (!step.subject.trim()) errors.push('Subject is required');

  if (step.offsetDays === '') {
    errors.push('Give this email a day');
  } else {
    // ⚠️ A START-anchored series cannot have a step before day 0 — there is
    // nothing before the day you enrol. The server refuses it too; saying so
    // here names the FIELD, which the 422 cannot.
    if (anchor === 'START' && step.offsetDays < 0) {
      errors.push('A series that starts at enrolment cannot send before day 0');
    }
    if (
      step.offsetDays < SEQUENCE_LIMITS.offsetMin ||
      step.offsetDays > SEQUENCE_LIMITS.offsetMax
    ) {
      errors.push('Day must be within a year of the anchor');
    }
  }

  // ⚠️ Checked here because the server's 422 NAMES NO STEP. Across thirty
  // emails "one email cannot exceed 256KB" is unactionable; the editor knows
  // which body is oversized and can say so beside it.
  if (step.content.length > SEQUENCE_LIMITS.contentMax) {
    errors.push(
      `This email is too long (${Math.round(step.content.length / 1000)}KB of ${
        SEQUENCE_LIMITS.contentMax / 1000
      }KB). Shorten it or split it across two steps.`
    );
  }

  const bad = [...findInvalidTokens(step.content), ...findInvalidTokens(step.subject)];
  if (bad.length) errors.push(`Unknown merge field: ${bad.join(', ')}`);
  return errors;
}

/**
 * Indices of steps sharing a day with another step.
 *
 * ⚠️ Only ONE email goes out per enrolment per school-day, so two steps on the
 * same day means one of them silently never sends. Returned as a set of BOTH
 * offenders — flagging only the second would point at the wrong email.
 */
export function findDuplicateDays(steps: SequenceStepFormState[]): Set<number> {
  const seen = new Map<number, number>();
  const dupes = new Set<number>();
  steps.forEach((s, i) => {
    if (s.offsetDays === '') return;
    const prior = seen.get(s.offsetDays);
    if (prior !== undefined) {
      dupes.add(prior);
      dupes.add(i);
    } else {
      seen.set(s.offsetDays, i);
    }
  });
  return dupes;
}

/** Move a step within the list. Returns the SAME array when the move is a no-op. */
export function reorderSteps(
  steps: SequenceStepFormState[],
  index: number,
  direction: -1 | 1
): SequenceStepFormState[] {
  const to = index + direction;
  if (to < 0 || to >= steps.length) return steps;
  const next = [...steps];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

// ===========================================
// PAGE
// ===========================================

export default function EmailSequenceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !id || id === 'new';

  const { data: saved, isLoading, isError } = useEmailSequence(isNew ? undefined : id);
  const { data: status } = useEmailSequenceStatus();
  const { data: enrolments = [] } = useSequenceEnrolments(isNew ? undefined : id);
  const createMutation = useCreateEmailSequence();
  const updateMutation = useUpdateEmailSequence();
  const testMutation = useSendEmailSequenceTest();
  const pauseMutation = usePauseSequenceEnrolment();
  const resumeMutation = useResumeSequenceEnrolment();
  const cancelMutation = useCancelSequenceEnrolment();

  const [form, setForm] = useState<EmailSequenceFormState>({
    name: '',
    description: '',
    anchor: 'START',
    sendHour: 9,
    isActive: true,
    steps: [emptyStep()],
  });
  const [activeStep, setActiveStep] = useState(0);
  // ⚠️ A BOOLEAN, not a second copy of "which step". Holding the previewed
  // position separately from `activeStep` let them DRIFT: previewing step 0 then
  // clicking step 1 left the panel rendering step 0's email under step 1's
  // editor, with nothing saying so (/qa Phase G). The preview now always
  // follows the step being edited, so the two cannot disagree.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState<string | null>(null);
  const [stopTarget, setStopTarget] = useState<EmailSequenceEnrolment | null>(null);
  const [serverInvalidFields, setServerInvalidFields] = useState<string[]>([]);

  const subjectRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const preheaderRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const bodyRefs = useRef<Record<number, RichTextEditorHandle | null>>({});

  // Hydrate the form once the record arrives.
  useEffect(() => {
    if (!saved) return;
    setForm({
      name: saved.name,
      description: saved.description ?? '',
      anchor: saved.anchor,
      sendHour: saved.sendHour,
      isActive: saved.isActive,
      steps: saved.steps.map((s) => ({
        offsetDays: s.offsetDays,
        subject: s.subject,
        preheader: s.preheader ?? '',
        content: s.content,
      })),
    });
  }, [saved]);

  const isArchived = Boolean(saved?.archivedAt);
  const dirty = isSequenceDirty(form, saved, isNew);
  // Covers tab-close, reload, browser Back and a typed URL. In-app sidebar
  // navigation is NOT covered — see the hook for why (`useBlocker` needs a
  // data router this app does not use) — and stays guarded by the Back button's
  // own confirm dialog below.
  useUnsavedChangesWarning(dirty);

  const { data: preview, isLoading: previewLoading, isError: previewError } =
    useEmailSequenceStepPreview(isNew || !previewOpen ? undefined : id, activeStep);

  // ===========================================
  // VALIDATION (client-side, mirroring the server's rules)
  // ===========================================

  const stepErrors = useMemo(
    () => form.steps.map((step) => validateStep(step, form.anchor)),
    [form.steps, form.anchor]
  );

  // ⚠️ Only ONE email may go out per day, so two steps on the same day would
  // silently mean one of them never sends. Caught as a pair, not per step.
  const duplicateDays = useMemo(() => findDuplicateDays(form.steps), [form.steps]);

  const hasErrors =
    !form.name.trim() || stepErrors.some((e) => e.length > 0) || duplicateDays.size > 0;

  // ===========================================
  // HANDLERS
  // ===========================================

  const patchStep = (index: number, patch: Partial<SequenceStepFormState>) => {
    setForm((prev) => ({
      ...prev,
      steps: prev.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));
    setServerInvalidFields([]);
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    const to = index + direction;
    if (to < 0 || to >= form.steps.length) return;
    setForm((prev) => ({ ...prev, steps: reorderSteps(prev.steps, index, direction) }));
    setActiveStep(to);
  };

  const insertToken = (
    e: React.MouseEvent,
    placeholder: string,
    target: 'subject' | 'preheader' | 'body'
  ) => {
    // Shared with the custom-email editor — see `utils/mergeFieldInsert`.
    if (isDuplicateMouseActivation(e)) return;
    e.preventDefault();

    if (target === 'body') {
      bodyRefs.current[activeStep]?.insertText(placeholder);
      return;
    }

    const input =
      target === 'subject' ? subjectRefs.current[activeStep] : preheaderRefs.current[activeStep];
    if (!input) {
      patchStep(activeStep, {
        [target]: form.steps[activeStep][target] + placeholder,
      } as Partial<SequenceStepFormState>);
      return;
    }

    const { value, caret } = insertAtSelectionEnd(input, placeholder);
    patchStep(activeStep, { [target]: value } as Partial<SequenceStepFormState>);
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(caret, caret);
    });
  };

  const handleSave = async () => {
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      anchor: form.anchor,
      sendHour: form.sendHour,
      isActive: form.isActive,
      steps: form.steps.map((s) => ({
        offsetDays: s.offsetDays === '' ? 0 : s.offsetDays,
        subject: s.subject.trim(),
        preheader: s.preheader.trim() || null,
        content: s.content,
      })),
    };

    try {
      if (isNew) {
        const created = await createMutation.mutateAsync(payload);
        enqueueSnackbar('Sequence created', { variant: 'success' });
        navigate(`/${schoolSlug}/admin/email-sequences/${created.id}`, { replace: true });
      } else {
        await updateMutation.mutateAsync({ sequenceId: id as string, data: payload });
        enqueueSnackbar('Sequence saved', { variant: 'success' });
      }
      setServerInvalidFields([]);
    } catch (err) {
      // ⚠️ Surface `invalidMergeFields` rather than only the message. The
      // payload names no step, so the tokens are matched back to steps below —
      // otherwise "unknown merge field" is unactionable across thirty emails.
      const details = (err as { response?: { data?: { details?: MergeFieldErrorDetails } } })
        .response?.data?.details;
      if (details?.invalidMergeFields?.length) {
        setServerInvalidFields(details.invalidMergeFields);
      }
      enqueueSnackbar(apiErrorMessage(err, 'Could not save this sequence'), { variant: 'error' });
    }
  };

  const handleTest = async () => {
    if (isNew || !id) return;
    try {
      const message = await testMutation.mutateAsync({
        sequenceId: id,
        data: { position: activeStep },
      });
      enqueueSnackbar(message, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(apiErrorMessage(err, 'Could not send the test email'), { variant: 'error' });
    }
  };

  const handleBack = () => {
    const target = `/${schoolSlug}/admin/email-sequences`;
    if (dirty) setLeaveTarget(target);
    else navigate(target);
  };

  const handleStopEnrolment = async () => {
    if (!stopTarget) return;
    try {
      await cancelMutation.mutateAsync(stopTarget.id);
      enqueueSnackbar('Series stopped. Emails already sent are not affected.', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(apiErrorMessage(err, 'Could not stop this series'), { variant: 'error' });
    }
    setStopTarget(null);
  };

  // ===========================================
  // RENDER
  // ===========================================

  if (!isNew && isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!isNew && isError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load this sequence.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/${schoolSlug}/admin/email-sequences`)}>
          Back to sequences
        </Button>
      </Box>
    );
  }

  const step = form.steps[activeStep];
  // ⚠️ DERIVED FROM THE STORED SIGN, not held as separate state. A second
  // source of truth for the same fact drifts — this feature has already been
  // bitten by that twice (the stale preview, the duplicated alias list).
  // A positive offset on a DATE sequence means "after the event"; 0 is neither.
  const stepAfter =
    form.anchor === 'DATE' && typeof step?.offsetDays === 'number' && step.offsetDays > 0;
  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <Box>
      <PageHeader
        title={isNew ? 'New Email Sequence' : form.name || 'Email Sequence'}
        subtitle="A series of emails sent to one family over time"
        action={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<BackIcon />} onClick={handleBack}>
              Back
            </Button>
            <PermissionGate resource="communications_emails" action="write">
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || hasErrors || isArchived}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      {isArchived && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This sequence is archived and can no longer be edited. Its enrolment history is kept below.
        </Alert>
      )}

      {status?.enabled === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Sending is switched off on this server</AlertTitle>
          This series can be saved and started, and will send once sending is enabled.
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* ----- HEADER ----- */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  fullWidth
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  disabled={isArchived}
                  inputProps={{ maxLength: SEQUENCE_LIMITS.nameMax }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Description"
                  fullWidth
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  disabled={isArchived}
                  inputProps={{ maxLength: SEQUENCE_LIMITS.descriptionMax }}
                  helperText="Only admins see this"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl disabled={isArchived}>
                  <FormLabel>When does it start?</FormLabel>
                  <RadioGroup
                    row
                    value={form.anchor}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, anchor: e.target.value as 'START' | 'DATE' }))
                    }
                  >
                    <FormControlLabel
                      value="START"
                      control={<Radio />}
                      label="When you enrol a student"
                    />
                    <FormControlLabel
                      value="DATE"
                      control={<Radio />}
                      label="Counting back from a date"
                    />
                  </RadioGroup>
                  <Typography variant="caption" color="text.secondary">
                    {form.anchor === 'START'
                      ? 'Day 0 is the day you start a student on this series.'
                      : 'You pick the date per student — an exam or a concert. Use negative days for "before".'}
                  </Typography>
                </FormControl>
              </Grid>

              <Grid item xs={6} md={3}>
                <FormControl fullWidth disabled={isArchived}>
                  <InputLabel>Sends at</InputLabel>
                  <Select
                    label="Sends at"
                    value={form.sendHour}
                    onChange={(e) => setForm((p) => ({ ...p, sendHour: Number(e.target.value) }))}
                  >
                    {Array.from(
                      { length: SEQUENCE_LIMITS.sendHourMax - SEQUENCE_LIMITS.sendHourMin + 1 },
                      (_, i) => SEQUENCE_LIMITS.sendHourMin + i
                    ).map((h) => (
                      <MenuItem key={h} value={h}>
                        {formatSendHour(h)}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    In your school's timezone
                  </Typography>
                </FormControl>
              </Grid>

              <Grid item xs={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                      disabled={isArchived}
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  An inactive series cannot be started for a new student.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ----- STEP LIST ----- */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 1 }}>
            <Stack spacing={0.5}>
              {form.steps.map((s, i) => (
                <Card
                  key={i}
                  variant={i === activeStep ? 'elevation' : 'outlined'}
                  onClick={() => setActiveStep(i)}
                  sx={{ cursor: 'pointer', bgcolor: i === activeStep ? 'action.selected' : undefined }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          {offsetCaption(s.offsetDays, form.anchor)}
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {s.subject || 'Untitled email'}
                        </Typography>
                      </Box>
                      {(stepErrors[i]?.length > 0 || duplicateDays.has(i)) && (
                        <Chip size="small" color="error" label="!" />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Button
              fullWidth
              size="small"
              startIcon={<AddIcon />}
              disabled={isArchived || form.steps.length >= SEQUENCE_LIMITS.stepsMax}
              onClick={() => {
                setForm((p) => ({ ...p, steps: [...p.steps, emptyStep()] }));
                setActiveStep(form.steps.length);
              }}
            >
              Add email
            </Button>
          </Paper>
        </Grid>

        {/* ----- STEP EDITOR ----- */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2 }}>
            {step && (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {/*
                    ⚠️ ON A DATE SEQUENCE THE ADMIN TYPES A PLAIN NUMBER AND IT
                    MEANS "BEFORE". Counting back IS the feature — you set an exam
                    or concert date and the family is prepared in the lead-up — so
                    "3" must mean three days before it, not after.

                    The stored value stays SIGNED (`-3`), because the backend adds
                    it to the anchor and the sweep, the snapshot and the migration
                    all depend on that. The sign is a STORAGE detail, converted at
                    this boundary only.

                    An earlier attempt kept the raw signed input and merely
                    labelled it "Day (− before)" with a nudge. That was the wrong
                    fix: it explained a trap instead of removing one, and left the
                    common case needing a minus sign that is easy to forget — with
                    the series then scheduling itself on the wrong side of the
                    event, silently. (Reported twice, 2026-09-18.)

                    "After the event" is still reachable, via the toggle below.
                  */}
                  <TextField
                    label={form.anchor === 'DATE' ? 'Days before' : 'Day'}
                    type="number"
                    size="small"
                    sx={{ width: 150 }}
                    value={displayOffset(step.offsetDays, form.anchor, stepAfter)}
                    disabled={isArchived}
                    onChange={(e) =>
                      patchStep(activeStep, {
                        offsetDays: storedOffset(
                          e.target.value,
                          form.anchor,
                          stepAfter
                        ),
                      })
                    }
                    inputProps={{ min: form.anchor === 'DATE' ? 0 : undefined }}
                    helperText={offsetCaption(step.offsetDays, form.anchor)}
                  />
                  {/*
                    The escape hatch for a "+1, how did it go?" follow-up, which
                    is a real but uncommon use. Off by default, so the common
                    case needs no thought.
                  */}
                  {form.anchor === 'DATE' && (
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={stepAfter}
                          disabled={isArchived}
                          onChange={(e) => {
                            const after = e.target.checked;
                            const magnitude =
                              step.offsetDays === '' ? '' : Math.abs(step.offsetDays);
                            patchStep(activeStep, {
                              offsetDays:
                                magnitude === '' ? '' : after ? magnitude : -magnitude,
                            });
                          }}
                        />
                      }
                      label={<Typography variant="caption">After the event</Typography>}
                    />
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Move earlier in the list">
                    <span>
                      <IconButton
                        size="small"
                        disabled={isArchived || activeStep === 0}
                        onClick={() => moveStep(activeStep, -1)}
                        aria-label="Move step up"
                      >
                        <UpIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move later in the list">
                    <span>
                      <IconButton
                        size="small"
                        disabled={isArchived || activeStep === form.steps.length - 1}
                        onClick={() => moveStep(activeStep, 1)}
                        aria-label="Move step down"
                      >
                        <DownIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove this email">
                    <span>
                      <IconButton
                        size="small"
                        disabled={isArchived || form.steps.length <= SEQUENCE_LIMITS.stepsMin}
                        onClick={() => {
                          setForm((p) => ({
                            ...p,
                            steps: p.steps.filter((_, i) => i !== activeStep),
                          }));
                          setActiveStep((a) => Math.max(0, a - 1));
                        }}
                        aria-label="Delete step"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                {duplicateDays.has(activeStep) && (
                  <Alert severity="error">
                    Another email in this series is also on day {String(step.offsetDays)}. Only one
                    email is sent per day, so give each its own day.
                  </Alert>
                )}
                {stepErrors[activeStep]?.map((message) => (
                  <Alert key={message} severity="error">
                    {message}
                  </Alert>
                ))}
                {serverInvalidFields.length > 0 && (
                  <Alert severity="error">
                    The server rejected these merge fields: {serverInvalidFields.join(', ')}
                  </Alert>
                )}

                <Box>
                  <TextField
                    label="Subject"
                    fullWidth
                    required
                    value={step.subject}
                    disabled={isArchived}
                    inputRef={(el) => (subjectRefs.current[activeStep] = el)}
                    onChange={(e) => patchStep(activeStep, { subject: e.target.value })}
                    inputProps={{ maxLength: SEQUENCE_LIMITS.subjectMax }}
                  />
                  <MergeFieldChips
                    fields={SEQUENCE_MERGE_FIELD_CHIPS}
                    target="subject"
                    onInsert={insertToken}
                    disabled={isArchived}
                  />
                </Box>

                <Box>
                  <TextField
                    label="Preheader"
                    fullWidth
                    value={step.preheader}
                    disabled={isArchived}
                    inputRef={(el) => (preheaderRefs.current[activeStep] = el)}
                    onChange={(e) => patchStep(activeStep, { preheader: e.target.value })}
                    inputProps={{ maxLength: SEQUENCE_LIMITS.preheaderMax }}
                    helperText="The line a mail client shows after the subject"
                  />
                  <MergeFieldChips
                    fields={SEQUENCE_MERGE_FIELD_CHIPS}
                    target="preheader"
                    onInsert={insertToken}
                    disabled={isArchived}
                  />
                </Box>

                <Box>
                  <RichTextEditor
                    /*
                      ⚠️ KEYED PER STEP, AND THIS IS LOAD-BEARING — without it,
                      adding an email WIPES the body of the one you were writing.
                      (Reported 2026-09-18; measured against the real component.)

                      Quill is controlled: when `value` is swapped underneath it,
                      it resets its contents and FIRES `onChange` with the new
                      value — normalised to '' for an empty document. That call
                      carries the PREVIOUS render's closure, still bound to the
                      old `activeStep`, so the empty string lands on the step you
                      just left. Measured: `onChange calls after swap: [[0,""]]`.

                      The subject survives because a plain TextField does not
                      re-emit on a value swap, which is what made the loss look
                      arbitrary.

                      A `key` gives each step its own editor instance, so there
                      is no swap to re-emit on. Do not remove it to "reuse" the
                      instance — that is precisely the bug.
                    */
                    key={`step-editor-${activeStep}`}
                    ref={(el) => (bodyRefs.current[activeStep] = el)}
                    value={step.content}
                    onChange={(content) => patchStep(activeStep, { content })}
                    disabled={isArchived}
                    label="Email body"
                  />
                  <MergeFieldChips
                    fields={SEQUENCE_MERGE_FIELD_CHIPS}
                    target="body"
                    onInsert={insertToken}
                    disabled={isArchived}
                  />
                  {/*
                    D7. Said plainly so nobody writes copy that promises
                    personalisation the data cannot support.
                  */}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Every family in this series receives the same steps — there is no practice data
                    to personalise on.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<PreviewIcon />}
                    onClick={() => setPreviewOpen((open) => !open)}
                    disabled={isNew}
                  >
                    Preview
                  </Button>
                  <PermissionGate resource="communications_emails" action="write">
                    <Button
                      size="small"
                      startIcon={<SendIcon />}
                      onClick={handleTest}
                      disabled={isNew || testMutation.isPending}
                    >
                      Send test to me
                    </Button>
                  </PermissionGate>
                  {isNew && (
                    <Typography variant="caption" color="text.secondary" alignSelf="center">
                      Save first to preview or send a test
                    </Typography>
                  )}
                </Stack>

                {previewOpen && (
                  <RenderedPreviewPanel
                    // Names the step it is showing, so the panel can never be
                    // read as belonging to a different email than the one open.
                    title={`Preview — ${offsetCaption(step.offsetDays, form.anchor)}`}
                    subject={preview?.subject}
                    html={preview?.html}
                    isLoading={previewLoading}
                    isError={previewError}
                  />
                )}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* ----- ENROLMENTS (G4b) ----- */}
        {!isNew && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Students on this series
              </Typography>
              {enrolments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nobody has been started on this series yet. Start one from a student's page.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Next email</TableCell>
                        <TableCell align="right">Sent</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell align="right">Skipped</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {enrolments.map((enrolment) => {
                        const next = enrolment.steps.find((s) => s.state === null);
                        return (
                          <TableRow key={enrolment.id}>
                            <TableCell>{enrolment.studentName}</TableCell>
                            <TableCell>
                              <EnrolmentStatusCell enrolment={enrolment} />
                            </TableCell>
                            <TableCell>
                              {enrolment.status === 'ACTIVE' && next ? (
                                <>
                                  <Typography variant="body2">{next.subject}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatStepDay(next)}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{enrolment.counts.sent}</TableCell>
                            <TableCell align="right">
                              {enrolment.counts.failed > 0 ? (
                                <Typography variant="body2" color="error">
                                  {enrolment.counts.failed}
                                </Typography>
                              ) : (
                                0
                              )}
                            </TableCell>
                            <TableCell align="right">{enrolment.counts.skipped}</TableCell>
                            <TableCell align="right">
                              <PermissionGate resource="communications_emails" action="write">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  {enrolment.status === 'ACTIVE' && (
                                    <Button
                                      size="small"
                                      onClick={() => pauseMutation.mutate(enrolment.id)}
                                    >
                                      Pause
                                    </Button>
                                  )}
                                  {enrolment.status === 'PAUSED' && (
                                    <Button
                                      size="small"
                                      onClick={() => resumeMutation.mutate(enrolment.id)}
                                    >
                                      Resume
                                    </Button>
                                  )}
                                  {(enrolment.status === 'ACTIVE' ||
                                    enrolment.status === 'PAUSED') && (
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() => setStopTarget(enrolment)}
                                    >
                                      Stop
                                    </Button>
                                  )}
                                </Stack>
                              </PermissionGate>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <ConfirmDialog
        open={Boolean(leaveTarget)}
        title="Leave without saving?"
        message="This sequence has unsaved changes. Leaving now discards them."
        confirmLabel="Discard changes"
        confirmColor="error"
        onConfirm={() => {
          const target = leaveTarget;
          setLeaveTarget(null);
          if (target) navigate(target);
        }}
        onCancel={() => setLeaveTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(stopTarget)}
        title="Stop this series?"
        message={`${stopTarget?.studentName ?? 'This student'} will receive no further emails from this series. Emails already sent are not affected, and this cannot be undone — starting them again is a fresh series.`}
        confirmLabel="Stop series"
        confirmColor="error"
        onConfirm={handleStopEnrolment}
        onCancel={() => setStopTarget(null)}
        loading={cancelMutation.isPending}
      />
    </Box>
  );
}

// ===========================================
// HELPERS
// ===========================================

/** "Day 3" / "14 days before" — the offset in words rather than a bare number. */
export function displayOffset(
  stored: number | '',
  anchor: 'START' | 'DATE',
  after: boolean
): number | '' {
  if (stored === '') return '';
  if (anchor !== 'DATE') return stored;
  // Day 0 is "on the day" under either direction.
  return Math.abs(stored);
  void after;
}

/**
 * What a typed value STORES.
 *
 * ⚠️ Negated for a DATE sequence unless the admin has asked for "after".
 * A magnitude is taken so a pasted "-3" cannot double-negate into "3 days
 * after" — the direction comes from the toggle, never from a stray sign.
 */
export function storedOffset(
  typed: string,
  anchor: 'START' | 'DATE',
  after: boolean
): number | '' {
  if (typed === '') return '';
  const n = Number(typed);
  if (Number.isNaN(n)) return '';
  if (anchor !== 'DATE') return n;
  const magnitude = Math.abs(n);
  return after ? magnitude : -magnitude;
}

export function offsetCaption(offset: number | '', anchor: 'START' | 'DATE'): string {
  if (offset === '') return 'No day set';
  if (anchor === 'START') return offset === 0 ? 'On the day they start' : `Day ${offset}`;
  if (offset === 0) return 'On the day';
  return offset < 0
    ? `${Math.abs(offset)} day${Math.abs(offset) === 1 ? '' : 's'} before`
    : `${offset} day${offset === 1 ? '' : 's'} after`;
}

function EnrolmentStatusCell({ enrolment }: { enrolment: EmailSequenceEnrolment }) {
  if (enrolment.status === 'CANCELLED') {
    const copy = cancelReasonCopy(enrolment.cancelReason);
    return (
      <Chip
        size="small"
        variant="outlined"
        color={copy.warn ? 'warning' : 'default'}
        label={copy.text}
      />
    );
  }
  if (enrolment.status === 'COMPLETED') {
    // FILLED, so a finished series does not read as a running one now that both
    // are green. Mirrors `StudentEmailSequencesList.StatusChip` — the same
    // enrolment appears in both views and must look the same in each.
    return enrolment.counts.failed > 0 ? (
      <Chip size="small" variant="outlined" color="warning" label={completedSummary(enrolment)} />
    ) : (
      <Chip size="small" color="success" label={completedSummary(enrolment)} />
    );
  }
  if (enrolment.status === 'PAUSED') {
    return (
      <Tooltip title="Paused days are not made up — steps that fall due while paused go out one per day once resumed.">
        <Chip size="small" variant="outlined" label="Paused" />
      </Tooltip>
    );
  }
  // Green for a live record, matching the house convention (students use
  // `success` for ACTIVE) and the student-details view.
  return <Chip size="small" variant="outlined" color="success" label="Active" />;
}
